// Klaviyo client sign-ups: the password page's email and SMS form and the coming-soon email form.
// Both store the consent wording on the profile first, then subscribe it to their list, and report
// success only when every request returns 202. The footer newsletter imports `welcomeOffer` from here.
const SIGNUP_SOURCE = "aw26_prelaunch_20260908";
const KLAVIYO_REVISION = "2026-07-15";
const REQUEST_TIMEOUT = 15000;
export const LAUNCH_METRIC = "Requested Coming Soon Alert";

// Posts each request in order and throws unless Klaviyo accepts every one.
export async function postToKlaviyo(companyId, requests, signal) {
  for (const [endpoint, body] of requests) {
    signal.throwIfAborted();
    const response = await fetch(
      `https://a.klaviyo.com/client/${endpoint}/?company_id=${encodeURIComponent(companyId)}`,
      {
        method: "POST",
        credentials: "omit",
        headers: {
          "Content-Type": "application/vnd.api+json",
          Accept: "application/vnd.api+json",
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify(body),
        signal,
      },
    );
    if (response.status !== 202) throw new Error("Signup was not accepted.");
  }
}

// Tells the Klaviyo site script and storefront analytics who this browser belongs to, so later
// product views and carts reach the person who signed up. Queued when a script has not loaded yet.
export function identifyVisitor(email) {
  try {
    if (window.klaviyo?.identify) window.klaviyo.identify({ email });
    else (window._klOnsite = window._klOnsite || []).push(["identify", { email }]);
    // Storefront analytics links the same shopper by a hash of this address.
    (window.rofVisitorEmails = window.rofVisitorEmails || []).push(email);
    document.dispatchEvent(new CustomEvent("rof:visitor-email"));
  } catch {}
}

// The welcome offer a visitor was shown, so every sign-up gives the same one as the pop-up test. Klaviyo keeps
// the form version it assigned each visitor in localStorage `klaviyoOnsite` (the shape storefront-posthog.js
// reads). These are the $20 versions of the test's phone and desktop forms (V9G5aY, WRuGk5); anyone else, or
// anyone whose version can't be read, keeps the 15%. The welcome emails split on the profile property
// `rof_welcome_offer`, which the $20 pop-up sets to "usd20". Editing a live Klaviyo test clones it into new versions, so
// these numbers change with every edit; the earlier ones stay listed for visitors who haven't seen the form since.
const USD20_FORM_VERSIONS = ["29048124", "29048138", "29048540", "29048565", "29057050", "29057069"];

export function welcomeOffer() {
  try {
    const versions = JSON.parse(localStorage.getItem("klaviyoOnsite") || "null")?.viewedForms?.modal?.viewedForms;
    const shown = Object.values(versions && typeof versions === "object" ? versions : {}).map(String);
    return shown.some((version) => USD20_FORM_VERSIONS.includes(version)) ? "usd20" : "";
  } catch {
    return "";
  }
}

export function normalizePhone(value) {
  const phone = value.trim().replace(/[\s().-]/g, "");
  return !phone || /^\+[1-9]\d{7,14}$/.test(phone) ? phone : null;
}

export function subscriptionPayload({ email, phone, smsConsent, listId, submittedAt, disclosureVersion, emailNotice, smsDisclosure }) {
  if (smsConsent && !phone) throw new Error("A phone number is required for SMS consent.");
  const address = email.trim();
  const subscriptions = { email: { marketing: { consent: "SUBSCRIBED" } } };
  if (smsConsent) subscriptions.sms = { marketing: { consent: "SUBSCRIBED" } };
  const profile = {
    type: "profile",
    attributes: {
      email: address,
      ...(phone ? { phone_number: phone } : {}),
      subscriptions,
      properties: {
        rof_signup_source: SIGNUP_SOURCE,
        rof_prelaunch_phone: phone || "",
        rof_prelaunch_latest_consent: JSON.stringify({
          email: address,
          phone: phone || "",
          email_marketing: true,
          sms_marketing: Boolean(smsConsent),
          submitted_at: submittedAt,
          disclosure_version: disclosureVersion,
          email_notice: emailNotice,
          sms_disclosure: smsDisclosure,
        }),
      },
    },
  };
  return {
    data: {
      type: "subscription",
      attributes: {
        custom_source: SIGNUP_SOURCE,
        profile: { data: profile },
      },
      relationships: { list: { data: { type: "list", id: listId } } },
    },
  };
}

export class PasswordSignup extends HTMLElement {
  connectedCallback() {
    if (this.lifecycle) return;
    this.form = this.querySelector("form");
    this.fields = this.querySelector("fieldset");
    this.status = this.querySelector("[data-signup-status]");
    this.heading = this.closest(".password-signup")?.querySelector("h2");
    this.email = this.querySelector('[name="email"]');
    this.phone = this.querySelector('[name="phone"]');
    this.sms = this.querySelector('[name="sms_consent"]');
    this.phoneOptions = this.querySelector("[data-phone-options]");
    this.emailNotice = this.querySelector("[data-email-notice]");
    this.smsDisclosure = this.querySelector("[data-sms-disclosure]");
    if (!this.form || !this.fields || !this.status || !this.email || !this.phone || !this.sms ||
        !this.phoneOptions || !this.emailNotice || !this.smsDisclosure) return;
    if (!/^[a-zA-Z0-9]+$/.test(this.dataset.companyId || "") ||
        !/^[a-zA-Z0-9]+$/.test(this.dataset.listId || "") ||
        !this.dataset.disclosureVersion?.trim() ||
        !this.emailNotice.textContent.trim() || !this.smsDisclosure.textContent.trim()) {
      this.status.textContent = this.dataset.failure;
      return;
    }
    this.lifecycle = new AbortController();
    const options = { signal: this.lifecycle.signal };
    this.form.addEventListener("submit", (event) => this.submit(event), options);
    this.phone.addEventListener("input", () => {
      this.phone.setCustomValidity("");
      this.updatePhoneOptions();
    }, options);
    this.sms.addEventListener("change", () => this.phone.setCustomValidity(""), options);
    this.updatePhoneOptions();
    if (!this.completed) this.fields.disabled = false;
  }

  updatePhoneOptions() {
    this.phoneOptions.hidden = !this.phone.value.trim();
    this.sms.required = !this.phoneOptions.hidden;
    if (this.phoneOptions.hidden) this.sms.checked = false;
  }

  disconnectedCallback() {
    this.lifecycle?.abort();
    this.request?.abort();
    this.lifecycle = null;
  }

  async submit(event) {
    event.preventDefault();
    if (this.pending || this.completed) return;
    const phone = normalizePhone(this.phone.value);
    this.sms.required = Boolean(this.phone.value.trim());
    this.phone.setCustomValidity(phone === null ? this.dataset.invalidPhone :
      this.sms.checked && !phone ? this.dataset.phoneRequired : "");
    if (!this.form.reportValidity()) return;
    const payload = subscriptionPayload({
      email: this.email.value,
      phone,
      smsConsent: this.sms.checked,
      listId: this.dataset.listId,
      submittedAt: new Date().toISOString(),
      disclosureVersion: this.dataset.disclosureVersion,
      emailNotice: { text: this.emailNotice.textContent.trim(), html: this.emailNotice.innerHTML },
      smsDisclosure: { text: this.smsDisclosure.textContent.trim(), html: this.smsDisclosure.innerHTML },
    });
    this.pending = true;
    this.fields.disabled = true;
    this.form.setAttribute("aria-busy", "true");
    this.status.textContent = this.dataset.submitting;
    this.status.setAttribute("role", "status");
    const request = new AbortController();
    this.request = request;
    const timer = setTimeout(() => request.abort(), REQUEST_TIMEOUT);
    try {
      const { email, properties } = payload.data.attributes.profile.data.attributes;
      const evidencePayload = { data: { type: "profile", attributes: { email, properties } } };
      await postToKlaviyo(this.dataset.companyId, [["profiles", evidencePayload], ["subscriptions", payload]], request.signal);
      identifyVisitor(email);
      this.completed = true;
      this.fields.hidden = true;
      if (this.heading && this.dataset.successHeading) this.heading.textContent = this.dataset.successHeading;
      this.status.textContent = "";
    } catch {
      if (this.isConnected) {
        this.status.setAttribute("role", "alert");
        this.status.textContent = this.dataset.failure;
      }
    } finally {
      clearTimeout(timer);
      this.pending = false;
      this.request = null;
      this.form.removeAttribute("aria-busy");
      if (!this.completed) this.fields.disabled = false;
    }
  }
}

// The coming-soon form: email, plus the size on a product with sizes. A product form also records
// which product and size were requested, as a Klaviyo event, so the launch email can go to the
// people who asked for that piece and the team can see which sizes they want. The sign-up also joins
// the newsletter list, when one is set, so it gets the welcome emails like a footer sign-up, with the
// offer the visitor's pop-up showed (`offer`, from welcomeOffer).
export function launchRequests({ email, listId, newsletterListId, submittedAt, disclosureVersion, emailNotice, product, size, offer }) {
  const address = email.trim();
  const source = product ? `Coming soon: ${product.title}${size ? ` (${size.title})` : ""}` : "Coming soon page";
  const consent = {
    email: address,
    email_marketing: true,
    submitted_at: submittedAt,
    disclosure_version: disclosureVersion,
    email_notice: emailNotice,
    source,
  };
  const subscribe = (id) => ["subscriptions", {
    data: {
      type: "subscription",
      attributes: {
        custom_source: source,
        profile: { data: { type: "profile", attributes: { email: address, subscriptions: { email: { marketing: { consent: "SUBSCRIBED" } } } } } },
      },
      relationships: { list: { data: { type: "list", id } } },
    },
  }];
  const welcome = newsletterListId && newsletterListId !== listId && offer === "usd20" ? { rof_welcome_offer: offer } : {};
  const requests = [
    ["profiles", {
      data: { type: "profile", attributes: { email: address, properties: { rof_launch_latest_consent: JSON.stringify(consent), ...welcome } } },
    }],
    subscribe(listId),
  ];
  if (newsletterListId && newsletterListId !== listId) requests.push(subscribe(newsletterListId));
  if (product) {
    requests.push(["events", {
      data: {
        type: "event",
        attributes: {
          properties: {
            ProductID: product.id,
            ProductName: product.title,
            ProductHandle: product.handle,
            URL: product.url,
            ImageURL: product.image || "",
            ...(size ? { Size: size.title, VariantID: size.id, SKU: size.sku } : {}),
          },
          metric: { data: { type: "metric", attributes: { name: LAUNCH_METRIC } } },
          profile: { data: { type: "profile", attributes: { email: address } } },
          // One event per person and product (and size), however often they submit.
          unique_id: `coming-soon:${product.id}:${size ? `${size.id}:` : ""}${address.toLowerCase()}`,
        },
      },
    }]);
  }
  return requests;
}

export class LaunchSignup extends HTMLElement {
  connectedCallback() {
    if (this.lifecycle) return;
    this.form = this.querySelector("form");
    this.fields = this.querySelector("fieldset");
    this.status = this.querySelector("[data-signup-status]");
    this.email = this.querySelector('[name="email"]');
    this.emailNotice = this.querySelector("[data-email-notice]");
    if (!this.form || !this.fields || !this.status || !this.email || !this.emailNotice) return;
    // A product's sizes sit in the size picker's place, outside this element, tied to the form.
    this.sizes = [...this.form.elements].filter((element) => element.name === "variant");
    this.sizeRow = this.sizes[0]?.closest(".variant-picker__option-values");
    if (!/^[a-zA-Z0-9]+$/.test(this.dataset.companyId || "") ||
        !/^[a-zA-Z0-9]+$/.test(this.dataset.listId || "") ||
        !/^[a-zA-Z0-9]*$/.test(this.dataset.newsletterListId || "") ||
        !this.dataset.disclosureVersion?.trim() || !this.emailNotice.textContent.trim()) {
      this.status.textContent = this.dataset.failure;
      return;
    }
    this.lifecycle = new AbortController();
    const options = { signal: this.lifecycle.signal };
    this.form.addEventListener("submit", (event) => this.submit(event), options);
    for (const size of this.sizes) size.addEventListener("change", () => this.sizeChanged(), options);
    if (!this.completed) this.fields.disabled = false;
  }

  disconnectedCallback() {
    this.lifecycle?.abort();
    this.request?.abort();
    this.lifecycle = null;
    clearTimeout(this.pulseTimer);
  }

  get product() {
    const { productId: id, productHandle: handle, productTitle: title, productUrl: url, productImage: image } = this.dataset;
    return handle ? { id, handle, title, url, image } : null;
  }

  get size() {
    const input = this.sizes.find((size) => size.checked);
    return input ? { id: input.value, title: input.dataset.size, sku: input.dataset.sku || "" } : null;
  }

  // Choosing a size answers the prompt to choose one. After a sign-up, choosing another size offers
  // the form again for that size, as the back-in-stock form does.
  sizeChanged() {
    if (this.pending) return;
    if (this.completed) {
      this.completed = false;
      this.fields.hidden = false;
      this.fields.disabled = false;
      this.status.textContent = "";
    } else if (this.status.textContent === this.dataset.sizeRequired) {
      this.status.textContent = "";
    }
  }

  // The size row pulses and takes focus, as the product page's does when Add to cart is pressed first.
  askForSize() {
    this.status.setAttribute("role", "alert");
    this.status.textContent = this.dataset.sizeRequired;
    this.sizes[0].focus();
    if (!this.sizeRow) return;
    this.sizeRow.classList.remove("is-pulsing");
    void this.sizeRow.offsetWidth; // restarts the animation on a second press
    this.sizeRow.classList.add("is-pulsing");
    clearTimeout(this.pulseTimer);
    this.pulseTimer = setTimeout(() => this.sizeRow.classList.remove("is-pulsing"), 200);
  }

  async submit(event) {
    event.preventDefault();
    if (this.pending || this.completed) return;
    const size = this.size;
    if (this.sizes.length && !size) return this.askForSize();
    if (!this.form.reportValidity()) return;
    const product = this.product;
    const requests = launchRequests({
      email: this.email.value,
      listId: this.dataset.listId,
      newsletterListId: this.dataset.newsletterListId,
      submittedAt: new Date().toISOString(),
      disclosureVersion: this.dataset.disclosureVersion,
      emailNotice: { text: this.emailNotice.textContent.trim(), html: this.emailNotice.innerHTML },
      product,
      size,
      offer: welcomeOffer(),
    });
    this.pending = true;
    this.fields.disabled = true;
    this.form.setAttribute("aria-busy", "true");
    this.status.setAttribute("role", "status");
    this.status.textContent = this.dataset.submitting;
    const request = new AbortController();
    this.request = request;
    const timer = setTimeout(() => request.abort(), REQUEST_TIMEOUT);
    try {
      await postToKlaviyo(this.dataset.companyId, requests, request.signal);
      identifyVisitor(this.email.value);
      this.completed = true;
      this.fields.hidden = true;
      this.status.textContent = this.dataset.done;
      requestAnimationFrame(() => this.status.focus({ preventScroll: true }));
      this.dispatchEvent(new CustomEvent("rof:launch-signup", { bubbles: true, detail: { productHandle: product?.handle || null, size: size?.title || null } }));
    } catch {
      if (this.isConnected) {
        this.status.setAttribute("role", "alert");
        this.status.textContent = this.dataset.failure;
      }
    } finally {
      clearTimeout(timer);
      this.pending = false;
      this.request = null;
      this.form.removeAttribute("aria-busy");
      if (!this.completed) this.fields.disabled = false;
    }
  }
}

if (!customElements.get("password-signup")) {
  customElements.define("password-signup", PasswordSignup);
}
if (!customElements.get("launch-signup")) {
  customElements.define("launch-signup", LaunchSignup);
}
